import { useState, useCallback, useRef } from 'react'
import { requestPresignedUrl } from '../lib/api'

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error'

export interface UploadTrack {
  id: string
  status: UploadStatus
  progress: number
  s3Key: string | null
  error: string | null
}

export interface MultiUploadState {
  uploads: Record<string, UploadTrack>
  isAnyUploading: boolean
  isAnyError: boolean
  allSuccess: boolean
}

const createInitialTrack = (id: string): UploadTrack => ({
  id,
  status: 'idle',
  progress: 0,
  s3Key: null,
  error: null,
})

export function useMultiUpload() {
  const [uploads, setUploads] = useState<Record<string, UploadTrack>>({})
  const xhrRefs = useRef<Record<string, XMLHttpRequest>>({})

  // Compute derived state
  const uploadValues = Object.values(uploads)
  const isAnyUploading = uploadValues.some((u) => u.status === 'uploading')
  const isAnyError = uploadValues.some((u) => u.status === 'error')
  const allSuccess = uploadValues.length > 0 && uploadValues.every((u) => u.status === 'success')

  const startUpload = useCallback(async (id: string, file: File, email: string) => {
    // Cancel existing upload for this ID if any
    if (xhrRefs.current[id]) {
      xhrRefs.current[id].abort()
      delete xhrRefs.current[id]
    }

    // Initialize upload track
    setUploads((prev) => ({
      ...prev,
      [id]: {
        ...createInitialTrack(id),
        status: 'uploading',
      },
    }))

    try {
      // Request presigned URL
      const result = await requestPresignedUrl(email, file.type, id)

      // If endpoint not available, reset to idle (fallback to regular upload)
      if (result.error === 'Unable to connect to server') {
        console.log(`[MultiUpload] ${id}: Endpoint not available, falling back to regular upload`)
        setUploads((prev) => ({
          ...prev,
          [id]: createInitialTrack(id),
        }))
        return
      }

      if (result.error || !result.data) {
        setUploads((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            status: 'error',
            error: result.error || 'Failed to get upload URL',
          },
        }))
        return
      }

      const { uploadUrl, s3Key } = result.data

      // Upload using XHR for progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhrRefs.current[id] = xhr

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100)
            setUploads((prev) => ({
              ...prev,
              [id]: { ...prev[id], progress },
            }))
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploads((prev) => ({
              ...prev,
              [id]: {
                ...prev[id],
                status: 'success',
                progress: 100,
                s3Key,
              },
            }))
            resolve()
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`))
          }
        })

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'))
        })

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'))
        })

        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })
    } catch (error) {
      // Don't set error state if upload was cancelled
      if (error instanceof Error && error.message === 'Upload cancelled') {
        return
      }

      setUploads((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        },
      }))
    } finally {
      delete xhrRefs.current[id]
    }
  }, [])

  const cancelUpload = useCallback((id: string) => {
    if (xhrRefs.current[id]) {
      xhrRefs.current[id].abort()
      delete xhrRefs.current[id]
    }
    setUploads((prev) => {
      const newUploads = { ...prev }
      delete newUploads[id]
      return newUploads
    })
  }, [])

  const retryUpload = useCallback(
    (id: string, file: File, email: string) => {
      return startUpload(id, file, email)
    },
    [startUpload]
  )

  const reset = useCallback(() => {
    // Cancel all ongoing uploads
    Object.keys(xhrRefs.current).forEach((id) => {
      xhrRefs.current[id].abort()
    })
    xhrRefs.current = {}
    setUploads({})
  }, [])

  const dismissUpload = useCallback((id: string) => {
    setUploads((prev) => {
      const newUploads = { ...prev }
      delete newUploads[id]
      return newUploads
    })
  }, [])

  const getUpload = useCallback(
    (id: string): UploadTrack | undefined => {
      return uploads[id]
    },
    [uploads]
  )

  return {
    state: {
      uploads,
      isAnyUploading,
      isAnyError,
      allSuccess,
    },
    startUpload,
    cancelUpload,
    retryUpload,
    reset,
    dismissUpload,
    getUpload,
  }
}
